import React, { useContext, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import productData from "../Datas/productData";
import "../css/productdetails.scss";
import { CartContext } from "./CartContext";
import siteConfig, { formatPrice } from "../config/siteConfig";

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, wishlist, toggleWishlist } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (!product) return;
    setSelectedSize(product.size[0] || "");
    setSelectedColor(product.colors[0] || "");
    setQuantity(1);
    setSelectedImage(0);
    setImageLoaded(false);
    setImageFailed(false);
    setAdded(false);
    document.title = `${product.name} — ${siteConfig.brandName}`;
    window.scrollTo({ top: 0 });
  }, [product]);

  useEffect(() => {
    const closeZoom = (event) => {
      if (event.key === "Escape") setZoomOpen(false);
    };
    window.addEventListener("keydown", closeZoom);
    return () => window.removeEventListener("keydown", closeZoom);
  }, []);

  if (!product) {
    return (
      <main className="product-not-found">
        <p>404</p>
        <h1>Product not found</h1>
        <Link to="/shop">Back to the collection</Link>
      </main>
    );
  }

  const wished = wishlist.includes(product.id);
  const related = productData
    .filter((item) => item.id !== product.id && item.category === product.category)
    .slice(0, 4);

  const handleAddToCart = () => {
    addToCart(product.id, quantity, selectedSize, selectedColor);
    setAdded(true);
  };

  return (
    <main className="product-detail">
      <div className="product-detail__layout">
        <section className="product-gallery" aria-label="Product images">
          <div className="product-gallery__main">
            {!imageLoaded && !imageFailed && <span className="product-gallery__skeleton" />}
            {imageFailed ? (
              <span className="product-gallery__error">Image unavailable</span>
            ) : (
              <button type="button" onClick={() => setZoomOpen(true)} aria-label="Open image zoom">
                <img
                  src={product.images[selectedImage]}
                  alt={product.name}
                  className={imageLoaded ? "is-loaded" : ""}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageFailed(true)}
                />
                <span>＋</span>
              </button>
            )}
          </div>
          {product.images.length > 1 && (
            <div className="product-gallery__thumbs">
              {product.images.map((image, index) => (
                <button
                  key={image}
                  type="button"
                  className={index === selectedImage ? "is-active" : ""}
                  onClick={() => {
                    setSelectedImage(index);
                    setImageLoaded(false);
                  }}
                >
                  <img src={image} alt={`${product.name} view ${index + 1}`} />
                </button>
              ))}
            </div>
          )}
        </section>

        <aside className="product-info">
          <p className="product-info__collection">{product.collection}</p>
          <h1>{product.name}</h1>
          <p className="product-info__price">{formatPrice(product.price)}</p>
          <p className="product-info__tax">Tax included. Shipping calculated at checkout.</p>

          <div className="product-option">
            <div className="product-option__label">
              <span>Color</span>
              <strong>{selectedColor}</strong>
            </div>
            <div className="product-option__values product-option__values--colors">
              {product.colors.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={color === selectedColor ? "is-selected" : ""}
                  onClick={() => setSelectedColor(color)}
                >
                  {color}
                </button>
              ))}
            </div>
          </div>

          <div className="product-option">
            <div className="product-option__label">
              <span>Size</span>
              <Link to="/size-guide">Size guide</Link>
            </div>
            <div className="product-option__values">
              {product.size.map((size) => (
                <button
                  key={size}
                  type="button"
                  className={size === selectedSize ? "is-selected" : ""}
                  onClick={() => setSelectedSize(size)}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          <div className="product-buy">
            <div className="product-quantity" aria-label="Quantity">
              <button type="button" onClick={() => setQuantity((value) => Math.max(1, value - 1))} aria-label="Decrease quantity">−</button>
              <span>{quantity}</span>
              <button type="button" onClick={() => setQuantity((value) => Math.min(10, value + 1))} aria-label="Increase quantity">＋</button>
            </div>
            <button className="product-buy__add" type="button" onClick={handleAddToCart}>
              {added ? "Added to bag" : "Add to cart"}
            </button>
          </div>

          {added && (
            <div className="product-added" role="status">
              Added to your bag. <Link to="/cartpage">View bag</Link>
            </div>
          )}

          <button className={`product-wish ${wished ? "is-active" : ""}`} type="button" onClick={() => toggleWishlist(product.id)}>
            <span>{wished ? "♥" : "♡"}</span>
            {wished ? "Remove from wishlist" : "Add to wishlist"}
          </button>

          <p className="product-stock"><span />{product.stock <= 4 ? "A few left in stock" : "In stock and ready to dispatch"}</p>
          <p className="product-info__description">{product.description}</p>

          <dl className="product-attributes">
            <div><dt>Color</dt><dd>{product.colors.join(", ")}</dd></div>
            <div><dt>Composition</dt><dd>{product.material}</dd></div>
            {Object.entries(product.attributes).map(([key, value]) => (
              <div key={key}><dt>{key}</dt><dd>{value}</dd></div>
            ))}
          </dl>

          <details className="product-accordion">
            <summary>Details & care</summary>
            <ul>{product.Features.map((feature) => <li key={feature}>{feature}</li>)}</ul>
            <p><Link to="/care">Read the materials & care guide</Link></p>
          </details>
          <details className="product-accordion">
            <summary>Delivery & returns</summary>
            <p>Prepared in 2–5 business days. Tracked delivery is {formatPrice(siteConfig.shipping.standard)} per order.</p>
            <p><Link to="/shipping">Shipping details</Link> · <Link to="/returns">Return policy</Link></p>
          </details>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="product-related" aria-labelledby="related-title">
          <h2 id="related-title">You may also like</h2>
          <div>
            {related.map((item) => (
              <Link to={`/shopDetails/${item.id}`} key={item.id}>
                <img src={item.image} alt={item.name} loading="lazy" />
                <span>{item.name}</span>
                <small>{formatPrice(item.price)}</small>
              </Link>
            ))}
          </div>
        </section>
      )}

      {zoomOpen && (
        <div className="product-zoom" role="dialog" aria-modal="true" aria-label={`${product.name} enlarged image`}>
          <button type="button" onClick={() => setZoomOpen(false)}>Close</button>
          <img src={product.images[selectedImage]} alt={product.name} />
        </div>
      )}
    </main>
  );
};

export default ProductDetail;
